/*
* AbstractPdfDocument.js
* 
* Uses jsPDF library from https://parall.ax/products/jspdf
* 
* For documentation on jsPDF, see:
* http://rawgit.com/MrRio/jsPDF/master/docs/index.html
* 
* Created on 30-11-2017
* @author Olivier for &ranj
* 
*/

import jsPDF from "jspdf";
import { isNull } from "../../util/TypeUtil";
import PdfPageBreakContent from "../content/generic/PdfPageBreakContent";
import PdfConfig from "../PdfConfig";
import PdfEvent from "../PdfEvent";

export default class AbstractPdfDocument {
    /**
     * @constructor
     * @param {soma.EventDispatcher} dispatcher 
     * @param {PdfConfig} pdfConfig 
     */
    constructor(dispatcher, pdfConfig) {
        this._dispatcher = dispatcher;
        this._pdfConfig = pdfConfig;

        this._document = null;
        this._filename = "PdfDocument";
        this._sections = [];
    }

    initialize() {
        this._pageHeight = this._pdfConfig.pageHeight;
        this._pageWidth = this._pdfConfig.pageWidth;
        this._pageMarginH = this._pdfConfig.pageMarginH;
        this._pageMarginV = this._pdfConfig.pageMarginV;
        this._footerY = this._pdfConfig.footerY;
    }

    destroy() {
        this._document = null;
        this._dispatcher = null;
        this._pdfConfig = null;
        this._sections = null;
    }

    /**
     * Generates the pdf document
     * The generation process is divided in iterations with slight delays between them, 
     *  this is to prevent the entire webpage from freezing while the document is generating. 
     * Alternatively, webhelpers could be used to implement true asynchronous document generation
     *  
     * For more info on parameters, see http://rawgit.com/MrRio/jsPDF/master/docs/jsPDF.html
     * @param {type} orientation Optional. One of "portrait" or "landscape" (or shortcuts "p" (Default), "l") 
     * @param {type} unit Optional. Measurement unit to be used when coordinates are specified. One of "pt" (points), "mm" (Default), "cm", "in"
     * @param {type} pageFormat Optional. One of 'pageFormats' as shown below, default: a4
     * @returns {undefined}
     */
    generate(orientation, unit, pageFormat) {
        this.initialize();

        orientation = orientation || AbstractPdfDocument.DEFAULT_ORIENTATION;
        unit = unit || AbstractPdfDocument.DEFAULT_UNIT;
        pageFormat = pageFormat || AbstractPdfDocument.DEFAULT_PAGEFORMAT;;

        let doc = new jsPDF(orientation, unit, pageFormat);

        this._registerFonts(doc);

        let contentToPrint = [];

        this._dispatcher.dispatch(PdfEvent.GENERATE_REPORT_PROGRESS, { progress: 0 }, this);

        //calculate height and create array of all content that must be printed to pdf document
        let sectionLoop = function (i, length, delay) {
            setTimeout(function () {
                //content of the loop: initialize each section and calculate its height
                let t = this._sections[i];
                this._sections[i].initialize();
                this._sections[i].calculateHeight(doc);
                contentToPrint = contentToPrint.concat(this._sections[i].getContent()); //add content array of section to contentToPrint array

                var progress = i / this._sections.length * 50;
                this._dispatcher.dispatch(PdfEvent.GENERATE_REPORT_PROGRESS, { progress: progress }, this);

                //start next iteration of the delayed loop, or if the loop is finished, call the printContent function
                if (++i < length) {
                    sectionLoop(i, length, delay);
                } else {
                    this._printContent(doc, contentToPrint);
                }
            }.bind(this), delay);
        }.bind(this);

        sectionLoop(0, this._sections.length, 50); //start index, length of loop, delay between loop iterations
    }

    _printContent(doc, contentToPrint) {
        let totalContent = contentToPrint.length;

        let x = this._pageMarginH;
        let content;
        let contentBatch;
        let contentIndex = 0;

        //loop through contentToPrint array, draws it onto the PDF page and adds more pages if necessary
        const printLoop = function (i, pages, delay) {
            setTimeout(function () {
                //content of the loop
                if (i > 0) {
                    doc.addPage();
                }

                //Enable the following line to draw a border around the content area of the page for debugging purposes
                //doc.rect(this._pageMarginH, this._pageMarginV, this._pageWidth, this._pageHeight);

                let heightLeft = this._pageHeight;
                let currentHeight = this._pageMarginV;

                //retrieves next batch of printable content, checks if it fits on the current page, then prints it onto the page
                while (this._contentBatchFitsOnPage(contentToPrint, contentIndex, heightLeft)) {
                    contentBatch = this._getNextContentBatch(contentToPrint, contentIndex);

                    //draw content batch onto the page
                    for (let ci = contentIndex; ci < contentIndex + contentBatch.items; ci++) {
                        content = contentToPrint[ci];
                        content.printToDocument(doc, x, currentHeight);
                        currentHeight += content.getFullHeight();
                    }

                    contentIndex = contentIndex + contentBatch.items;
                    heightLeft -= contentBatch.height;

                    //stop drawing on this page if there's a hard page break
                    if (contentBatch.forceBreak) {
                        break;
                    }
                }

                this._drawFooter(doc, i + 1);

                //update report generation progress
                var progress = (contentIndex / totalContent * 50);
                this._dispatcher.dispatch(PdfEvent.GENERATE_REPORT_PROGRESS, { progress: 50 + progress }, this);

                //if there's more content left to print to the document, add another page
                if (contentToPrint.length > contentIndex) {
                    pages++;
                }

                //start next iteration of the delayed loop, or if there's no more content to print, generation is complete
                if (++i < pages) {
                    printLoop(i, pages, delay);
                }
                else {
                    //generate report complete!
                    this._document = doc;
                    this._dispatcher.dispatch(PdfEvent.GENERATE_REPORT_COMPLETE, { document: doc }, this);
                }
            }.bind(this), delay);
        }.bind(this);

        printLoop(0, 1, 50); //start index, length of loop (increments automatically if there's more content to print), delay between loop iterations
    }

    isGenerated() {
        return !isNull(this._document);
    }

    addSection(section) {
        this._sections.push(section);
    }

    //Downloads the document. Exact behaviour differs per browser:
    //On Chrome and IE, the document is instantly downloaded to the browser's default download location
    //On Firefox, a "save as" window pops up - allowing the user to cancel or choose the download location
    //On Safari 11.0+ it works the same as on Chrome and IE, but on lower versions downloading doesn't work -- it opens the document in a new browser tab instead
    download() {
        if (this.isGenerated()) {
            this._document.output('save', this._filename);
        } else {
            console.warn("[AbstractPdfDocument] Can't download the document when it isn't generated yet.");
        }
    }

    //shows the document in a new tab. It's difficult for users to actually save the document to their desktop this way, so it's recommended to only use this for debugging purposes.
    show() {
        if (this.isGenerated()) {
            var string = this._document.output('datauristring');
            var object = '<object width="100%" height="100%" data="' + string + '" type="application/pdf"></object>';

            var x = window.open();
            x.document.open();
            x.document.write(object);
            x.document.close();
        } else {
            console.warn("[AbstractPdfDocument] Can't show the document when it isn't generated yet.");
        }
    }

    setFilename(filename) {
        this._filename = filename;
    }

    //register fonts specified in pdf_config.json, in order to add a font, it first needs to be compiled into the "jspdf/dist/default_vfs.js" file using the jsPDF customfonts "makeFonts" command
    _registerFonts(doc) {
        var fonts = this._pdfConfig.getFonts();
        //register each font with both lower case and capitalized names
        for (var i = 0; i < fonts.length; i++) {
            var font = fonts[i];
            var lowercase = font[1].toLowerCase();
            var capitalized = lowercase.charAt(0).toUpperCase() + lowercase.slice(1);

            doc.addFont(font[0], lowercase, font[2]);
            doc.addFont(font[0], capitalized, font[2]);
        }
    }

    //gets the next batch of content (until the next page break)
    _getNextContentBatch(contentToPrint, index) {
        var items = 0;
        var height = 0;
        var forceBreak = false;
        for (var i = index; i < contentToPrint.length; i++) {

            height += contentToPrint[i].getFullHeight();
            items++;

            if (contentToPrint[i] instanceof PdfPageBreakContent) {
                forceBreak = contentToPrint[i].isHardPageBreak();
                break;
            }
        }

        return { items: items, height: height, forceBreak: forceBreak };
    }

    //returns whether the next batch of content (until the next page break) fits on the page
    _contentBatchFitsOnPage(contentToPrint, index, heightLeft) {
        if (contentToPrint.length <= index) return false;

        var contentBatch = this._getNextContentBatch(contentToPrint, index);

        if (heightLeft === this._pageHeight + this._pageMarginV && contentBatch.height > this._pageHeight) {
            console.warn("[PdfDocument] Unbreakable section found that is larger than an entire PDF page!");
            console.log("Index:", index, "Content section:", contentBatch, "Content array:", contentToPrint);
            return true;
        }
        return contentBatch.height <= heightLeft;
    }

    //footer drawing code, executed once for every PDF page, override in child document
    _drawFooter(doc, pageIndex) { }
}

//See generate for more info:
AbstractPdfDocument.DEFAULT_ORIENTATION = "p";
AbstractPdfDocument.DEFAULT_UNIT = "px";
AbstractPdfDocument.DEFAULT_PAGEFORMAT = "a4";